import { ReportTemplate, TiptapJSON } from '../types/template.types';
import { greekMedicalGeneticsHeader, standardFooter } from './header-template';

/**
 * Create a section wrapper with standard attributes
 * @param sectionId - Unique section identifier
 * @param sectionType - Section type for categorization
 * @param title - Section title text
 * @param editable - Whether section is editable
 * @param prefillable - Whether section can be auto-populated
 * @param content - Section content nodes
 * @returns Tiptap JSON section node
 */
function createSection(
  sectionId: string,
  sectionType: string,
  title: string,
  editable: boolean = true,
  prefillable: boolean = false,
  content: TiptapJSON[] = []
): TiptapJSON {
  return {
    type: 'div',
    attrs: {
      style: 'margin-bottom: 20px;',
      sectionId,
      sectionType,
      editable,
      prefillable,
    },
    content: [
      {
        type: 'heading',
        attrs: { level: 3 },
        content: [
          {
            type: 'text',
            marks: [{ type: 'bold' }],
            text: title,
          },
        ],
      },
      ...content,
    ],
  };
}

/**
 * Patient information section (non-editable, auto-populated)
 * Compact table-based layout for better space efficiency
 */
const patientInfoSection: TiptapJSON = {
  type: 'div',
  attrs: {
    style: 'font-size: 10pt; line-height: 1.4;',
    sectionId: 'patient-info',
    sectionType: 'patient-info',
    editable: false,
    prefillable: true,
  },
  content: [
    {
      type: 'table',
      attrs: {
        style: 'width: 100%; border-collapse: collapse;',
      },
      content: [
        // Row 1: Patient Name (spanning) | Birth Date | Gender
        {
          type: 'tableRow',
          content: [
            {
              type: 'tableCell',
              attrs: {
                style:
                  'border: none; padding: 2px 8px 2px 0; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    {
                      type: 'text',
                      marks: [{ type: 'bold' }],
                      text: 'Ασθενής: ',
                    },
                    { type: 'text', text: '{{patient.name}}' },
                  ],
                },
              ],
            },
            {
              type: 'tableCell',
              attrs: {
                style: 'border: none; padding: 2px 8px; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    {
                      type: 'text',
                      marks: [{ type: 'bold' }],
                      text: 'Γέννηση: ',
                    },
                    { type: 'text', text: '{{patient.birthDate}}' },
                  ],
                },
              ],
            },
            {
              type: 'tableCell',
              attrs: {
                style:
                  'border: none; padding: 2px 0 2px 8px; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    { type: 'text', marks: [{ type: 'bold' }], text: 'Φύλο: ' },
                    { type: 'text', text: '{{patient.gender}}' },
                  ],
                },
              ],
            },
          ],
        },
        // Row 2: AMKA | Order # | Order Date
        {
          type: 'tableRow',
          content: [
            {
              type: 'tableCell',
              attrs: {
                style:
                  'border: none; padding: 2px 8px 2px 0; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    { type: 'text', marks: [{ type: 'bold' }], text: 'ΑΜΚΑ: ' },
                    { type: 'text', text: '{{patient.identifier}}' },
                  ],
                },
              ],
            },
            {
              type: 'tableCell',
              attrs: {
                style: 'border: none; padding: 2px 8px; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    {
                      type: 'text',
                      marks: [{ type: 'bold' }],
                      text: 'Αρ. Παραγγελίας: ',
                    },
                    { type: 'text', text: '{{order.identifier}}' },
                  ],
                },
              ],
            },
            {
              type: 'tableCell',
              attrs: {
                style:
                  'border: none; padding: 2px 0 2px 8px; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    {
                      type: 'text',
                      marks: [{ type: 'bold' }],
                      text: 'Ημ. Παραγγελίας: ',
                    },
                    { type: 'text', text: '{{order.orderDate}}' },
                  ],
                },
              ],
            },
          ],
        },
        // Row 3: Referring Doctor | Specimen Type | Collection Date
        {
          type: 'tableRow',
          content: [
            {
              type: 'tableCell',
              attrs: {
                style:
                  'border: none; padding: 2px 8px 2px 0; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    {
                      type: 'text',
                      marks: [{ type: 'bold' }],
                      text: 'Ιατρός: ',
                    },
                    { type: 'text', text: '{{order.requesterName}}' },
                  ],
                },
              ],
            },
            {
              type: 'tableCell',
              attrs: {
                style: 'border: none; padding: 2px 8px; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    {
                      type: 'text',
                      marks: [{ type: 'bold' }],
                      text: 'Δείγμα: ',
                    },
                    { type: 'text', text: '{{specimen.type}}' },
                  ],
                },
              ],
            },
            {
              type: 'tableCell',
              attrs: {
                style:
                  'border: none; padding: 2px 0 2px 8px; white-space: nowrap;',
              },
              content: [
                {
                  type: 'paragraph',
                  attrs: { style: 'margin: 0;' },
                  content: [
                    {
                      type: 'text',
                      marks: [{ type: 'bold' }],
                      text: 'Παραλαβή: ',
                    },
                    { type: 'text', text: '{{specimen.receivedDate}}' },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * Results section (editable)
 */
const resultsSection: TiptapJSON = createSection(
  'results',
  'results',
  'ΑΠΟΤΕΛΕΣΜΑΤΑ',
  true,
  false,
  [
    {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: 'Εισάγετε τα αποτελέσματα της ανάλυσης εδώ...',
        },
      ],
    },
    {
      type: 'table',
      content: [
        {
          type: 'tableRow',
          content: [
            {
              type: 'tableHeader',
              content: [
                {
                  type: 'paragraph',
                  content: [{ type: 'text', text: 'Γονίδιο' }],
                },
              ],
            },
            {
              type: 'tableHeader',
              content: [
                {
                  type: 'paragraph',
                  content: [{ type: 'text', text: 'Μετάλλαξη' }],
                },
              ],
            },
            {
              type: 'tableHeader',
              content: [
                {
                  type: 'paragraph',
                  content: [{ type: 'text', text: 'Αποτέλεσμα' }],
                },
              ],
            },
            {
              type: 'tableHeader',
              content: [
                {
                  type: 'paragraph',
                  content: [{ type: 'text', text: 'Ερμηνεία' }],
                },
              ],
            },
          ],
        },
        {
          type: 'tableRow',
          content: [
            {
              type: 'tableCell',
              content: [{ type: 'paragraph' }],
            },
            {
              type: 'tableCell',
              content: [{ type: 'paragraph' }],
            },
            {
              type: 'tableCell',
              content: [{ type: 'paragraph' }],
            },
            {
              type: 'tableCell',
              content: [{ type: 'paragraph' }],
            },
          ],
        },
      ],
    },
  ]
);

/**
 * Methodology section (editable)
 */
const methodologySection: TiptapJSON = createSection(
  'methodology',
  'methodology',
  'ΜΕΘΟΔΟΛΟΓΙΑ',
  true,
  false,
  [
    {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: 'Η ανάλυση πραγματοποιήθηκε με τη μέθοδο αλληλούχισης επόμενης γενιάς (NGS) χρησιμοποιώντας ένα πάνελ γονιδίων που σχετίζονται με νευροδερματικά σύνδρομα.',
        },
      ],
    },
    {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: 'Τα γονίδια που εξετάστηκαν περιλαμβάνουν:',
        },
      ],
    },
    {
      type: 'bulletList',
      content: [
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                { type: 'text', marks: [{ type: 'bold' }], text: 'TSC1, TSC2' },
                { type: 'text', text: ' - Οζώδης Σκλήρυνση' },
              ],
            },
          ],
        },
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                { type: 'text', marks: [{ type: 'bold' }], text: 'NF1' },
                { type: 'text', text: ' - Νευροϊνωμάτωση Τύπου 1' },
              ],
            },
          ],
        },
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'bold' }],
                  text: 'NF2, SMARCB1, LZTR1',
                },
                {
                  type: 'text',
                  text: ' - Νευροϊνωμάτωση Τύπου 2, Σβαννωμάτωση',
                },
              ],
            },
          ],
        },
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                { type: 'text', marks: [{ type: 'bold' }], text: 'SPRED1' },
                { type: 'text', text: ' - Σύνδρομο Legius' },
              ],
            },
          ],
        },
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'bold' }],
                  text: 'PTPN11, SOS1, RAF1, KRAS, BRAF, MAP2K1',
                },
                { type: 'text', text: ' - Σύνδρομα Noonan, LEOPARD' },
              ],
            },
          ],
        },
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                { type: 'text', marks: [{ type: 'bold' }], text: 'HRAS' },
                { type: 'text', text: ' - Σύνδρομο Costello' },
              ],
            },
          ],
        },
      ],
    },
  ]
);

/**
 * Observations section (editable)
 */
const observationsSection: TiptapJSON = createSection(
  'observations',
  'observations',
  'ΠΑΡΑΤΗΡΗΣΕΙΣ',
  true,
  false,
  [
    {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: 'Εισάγετε παρατηρήσεις και κλινικές σχολιασμούς εδώ...',
        },
      ],
    },
  ]
);

/**
 * References section (editable)
 */
const referencesSection: TiptapJSON = createSection(
  'references',
  'references',
  'ΒΙΒΛΙΟΓΡΑΦΙΚΕΣ ΑΝΑΦΟΡΕΣ',
  true,
  false,
  [
    {
      type: 'orderedList',
      content: [
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Northrup H, et al. Tuberous Sclerosis Complex Diagnostic Criteria Update. Pediatr Neurol. 2021.',
                },
              ],
            },
          ],
        },
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: 'Legius E, et al. Revised diagnostic criteria for neurofibromatosis type 1. J Med Genet. 2021.',
                },
              ],
            },
          ],
        },
      ],
    },
  ]
);

/**
 * Signatures section
 */
// const signaturesSection: TiptapJSON = {
//   type: 'div',
//   attrs: {
//     style: 'margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc;',
//     sectionId: 'signatures',
//     sectionType: 'signatures',
//     editable: true,
//   },
//   content: [
//     {
//       type: 'div',
//       attrs: {
//         style: 'display: flex; justify-content: space-between; gap: 40px;',
//       },
//       content: [
//         // Lab Director signature block
//         {
//           type: 'div',
//           attrs: {
//             style: 'flex: 1; text-align: center;',
//             sectionId: 'signature-lab-director',
//           },
//           content: [
//             {
//               type: 'paragraph',
//               content: [
//                 {
//                   type: 'text',
//                   marks: [{ type: 'bold' }],
//                   text: 'Διευθυντής Εργαστηρίου',
//                 },
//               ],
//             },
//             {
//               type: 'paragraph',
//               attrs: { textAlign: 'center' },
//               content: [
//                 {
//                   type: 'text',
//                   text: '{{signature.labDirector.name}}',
//                 },
//               ],
//             },
//             {
//               type: 'paragraph',
//               attrs: { textAlign: 'center' },
//               content: [
//                 {
//                   type: 'text',
//                   text: '{{signature.labDirector.title}}',
//                 },
//               ],
//             },
//             {
//               type: 'paragraph',
//               attrs: { textAlign: 'center' },
//               content: [
//                 {
//                   type: 'text',
//                   text: 'Ημερομηνία: {{signature.labDirector.date}}',
//                 },
//               ],
//             },
//           ],
//         },
//         // Responsible Doctor signature block
//         {
//           type: 'div',
//           attrs: {
//             style: 'flex: 1; text-align: center;',
//             sectionId: 'signature-responsible-doctor',
//           },
//           content: [
//             {
//               type: 'paragraph',
//               content: [
//                 {
//                   type: 'text',
//                   marks: [{ type: 'bold' }],
//                   text: 'Εργαστηριακός Γενετιστής',
//                 },
//               ],
//             },
//             {
//               type: 'paragraph',
//               attrs: { textAlign: 'center' },
//               content: [
//                 {
//                   type: 'text',
//                   text: '{{signature.responsibleDoctor.name}}',
//                 },
//               ],
//             },
//             {
//               type: 'paragraph',
//               attrs: { textAlign: 'center' },
//               content: [
//                 {
//                   type: 'text',
//                   text: '{{signature.responsibleDoctor.title}}',
//                 },
//               ],
//             },
//             {
//               type: 'paragraph',
//               attrs: { textAlign: 'center' },
//               content: [
//                 {
//                   type: 'text',
//                   text: 'Ημερομηνία: {{signature.responsibleDoctor.date}}',
//                 },
//               ],
//             },
//           ],
//         },
//       ],
//     },
//   ],
// };

/**
 * Complete Neurocutaneous Syndromes Report Template
 */
export const neurocutaneousTemplate: ReportTemplate = {
  id: 'neurocutaneous-syndromes',
  name: 'Έκθεση Νευροδερματικών Συνδρόμων',
  testCode: 'NEURO-CUTANEOUS-PANEL',
  description:
    'Πρότυπο έκθεσης για γενετική ανάλυση νευροδερματικών συνδρόμων (Οζώδης Σκλήρυνση, Νευροϊνωμάτωση, κ.λπ.)',
  version: '1.0.0',
  header: greekMedicalGeneticsHeader,
  footer: standardFooter,
  sections: [
    {
      id: 'patient-info',
      type: 'patient-info',
      label: 'ΣΤΟΙΧΕΙΑ ΑΣΘΕΝΟΥΣ',
      editable: false,
      prefillable: true,
      required: true,
      content: patientInfoSection,
    },
    {
      id: 'results',
      type: 'results',
      label: 'ΑΠΟΤΕΛΕΣΜΑΤΑ',
      editable: true,
      prefillable: false,
      required: true,
      placeholder: 'Εισάγετε τα αποτελέσματα της ανάλυσης...',
      content: resultsSection,
    },
    {
      id: 'methodology',
      type: 'methodology',
      label: 'ΜΕΘΟΔΟΛΟΓΙΑ',
      editable: true,
      prefillable: false,
      required: true,
      content: methodologySection,
    },
    {
      id: 'observations',
      type: 'observations',
      label: 'ΠΑΡΑΤΗΡΗΣΕΙΣ',
      editable: true,
      prefillable: false,
      required: false,
      placeholder: 'Εισάγετε παρατηρήσεις και σχόλια...',
      content: observationsSection,
    },
    {
      id: 'references',
      type: 'references',
      label: 'ΒΙΒΛΙΟΓΡΑΦΙΚΕΣ ΑΝΑΦΟΡΕΣ',
      editable: true,
      prefillable: false,
      required: false,
      content: referencesSection,
    },
    // {
    //   id: 'signatures',
    //   type: 'signatures',
    //   label: 'ΥΠΟΓΡΑΦΕΣ',
    //   editable: true,
    //   prefillable: true,
    //   required: true,
    //   content: signaturesSection,
    // },
  ],
  signers: [
    {
      role: 'lab-director',
      title: 'Διευθυντής Εργαστηρίου',
      required: true,
    },
    {
      role: 'responsible-doctor',
      title: 'Υπεύθυνος Ιατρός',
      required: true,
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

/**
 * Build full document content from template
 * @param template - Report template to build document from
 * @returns Complete Tiptap document JSON
 */
export function buildTemplateDocument(template: ReportTemplate): TiptapJSON {
  return {
    type: 'doc',
    content: [
      // Header (non-editable)
      template.header,
      // Report title
      {
        type: 'heading',
        attrs: { level: 2, textAlign: 'center' },
        content: [
          {
            type: 'text',
            marks: [{ type: 'bold' }],
            text: 'ΕΚΘΕΣΗ ΕΡΓΑΣΤΗΡΙΑΚΗΣ ΑΝΑΛΥΣΗΣ ΓΙΑ ΝΕΥΡΟΔΕΡΜΑΤΙΚΑ ΣΥΝΔΡΟΜΑ',
          },
        ],
      },
      // Subtitle
      {
        type: 'paragraph',
        attrs: { textAlign: 'center' },
        content: [
          {
            type: 'text',
            marks: [{ type: 'italic' }],
            text: '(ΟΖΩΔΗΣ ΣΚΛΗΡΥΝΣΗ, ΝΕΥΡΟΪΝΩΜΑΤΩΣΗ ΤΥΠΟΥ 1, ΤΥΠΟΥ 2, ΣΒΑΝΝΩΜΑΤΩΣΗ, ΣΥΝΔΡΟΜΑ LEGIUS, NOONAN, LEOPARD, COSTELLO)',
          },
        ],
      },
      // Sections
      ...template.sections.map((section) => section.content),
      // Footer (if present)
      ...(template.footer ? [template.footer] : []),
    ],
  };
}
